import React, { useEffect } from 'react';
import SelectInput from '../selectInput/SelectInput';
import TextInput from '../textInput/TextInput';
import Button from '../button/Button';
import TextButtonInput from '../textButtonInput/TextButtonInput';
import { SelectOpt } from '../CommonTypes';
import './FindAddress.scss';
import '../Common.scss';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'state/hooks';
import { selectIsEUSite, selectIsUSSite } from 'state/selectors/site';

export type AddressDetails = {
    addressList?: string;
    addressLine1?: string;
    addressLine2?: string;
    townOrCity?: string;
    region?: string;
    country?: string;
    postCode?: string;
};

type FindAddressProps = {
    showAddressList: boolean;
    showAddressForm: boolean;
    addressList: SelectOpt[];
    selectedAddress: string;
    addressErrors?: AddressDetails;
    address?: AddressDetails;
    searchPostCode?: string;
    searchPostCodeError?: string;
    finding: boolean;
    countries: SelectOpt[];
    onSelectAddress: (selectedValue: string) => void;
    onChangeDetail: (
        event: React.FormEvent<HTMLInputElement>,
        key: string
    ) => void;
    onShowAddressFormClick: () => void;
    onFindClick: () => void;
    onChangeSearchPostCode: (event: React.FormEvent<HTMLInputElement>) => void;
    onCountryChange: (selectedValue: string) => void;
};

const FindAddress = ({
    showAddressList,
    showAddressForm,
    addressList,
    selectedAddress,
    addressErrors,
    address,
    searchPostCode,
    searchPostCodeError,
    finding,
    countries,
    onSelectAddress,
    onChangeDetail,
    onShowAddressFormClick,
    onFindClick,
    onChangeSearchPostCode,
    onCountryChange
}: FindAddressProps): JSX.Element => {
    const { t } = useTranslation();
    const isUsSite = useAppSelector(selectIsUSSite);
    const isEUSite = useAppSelector(selectIsEUSite);

    const getAddressList = (): JSX.Element => {
        let listPanel: JSX.Element = <></>;

        if (showAddressList === true) {
            listPanel = (
                <div className='col-auto'>
                    <SelectInput
                        label={t('select_address_label')}
                        name='address-list'
                        forceDefault
                        options={addressList}
                        onChange={onSelectAddress}
                        value={selectedAddress}
                        error={addressErrors?.addressList}
                    />
                </div>
            );
        }
        return listPanel;
    };

    const getAddressForm = (): JSX.Element => {
        let addressSection: JSX.Element = <></>;

        if (showAddressForm === true) {
            addressSection = (
                <>
                    <hr />
                    <div className='row'>
                        <div className='col-auto'>
                            <TextInput
                                name='addressLine1'
                                label={t('address_1_label')}
                                error={addressErrors?.addressLine1}
                                value={address?.addressLine1 || ''}
                                onChange={(e) =>
                                    onChangeDetail(e, 'addressLine1')
                                }
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-auto'>
                            <TextInput
                                name='addressLine2'
                                label={t('address_2_label')}
                                error={addressErrors?.addressLine2}
                                value={address?.addressLine2 || ''}
                                onChange={(e) =>
                                    onChangeDetail(e, 'addressLine2')
                                }
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col'>
                            <TextInput
                                name='townOrCity'
                                label={t('towncity_label')}
                                error={addressErrors?.townOrCity}
                                value={address?.townOrCity || ''}
                                onChange={(e) =>
                                    onChangeDetail(e, 'townOrCity')
                                }
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col'>
                            <TextInput
                                name='region'
                                label={t('region')}
                                error={addressErrors?.region}
                                value={address?.region || ''}
                                onChange={(e) => onChangeDetail(e, 'region')}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-auto'>
                            <SelectInput
                                name='country'
                                label={t('country')}
                                loadingText={t('select_country')}
                                value={address?.country}
                                options={countries}
                                onChange={onCountryChange}
                                forceDefault
                                addDefaultOption
                                defaultText={t('select_country')}
                                error={addressErrors?.country}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col'>
                            <TextInput
                                name='postcode'
                                label={t('postcode')}
                                className='small'
                                error={addressErrors?.postCode}
                                value={address?.postCode || ''}
                                onChange={(e) => onChangeDetail(e, 'postCode')}
                            />
                        </div>
                    </div>
                </>
            );
        }
        return addressSection;
    };

    const getManualAddressButton = (): JSX.Element => {
        let buttonPanel: JSX.Element = <></>;

        if (!showAddressForm) {
            buttonPanel = (
                <div className='row'>
                    <div className='col'>
                        <Button
                            onClick={onShowAddressFormClick}
                            text={t('enter_address_manually')}
                            name='show-address-form'
                            className='btn btn-primary button-theme-secondary'
                        />
                    </div>
                </div>
            );
        }

        return buttonPanel;
    };

    useEffect(() => {
        (isUsSite || isEUSite) && onShowAddressFormClick();
    }, []);

    const addressListPanel = getAddressList();
    const addressPanel = getAddressForm();
    const manualAddressButtonPanel = getManualAddressButton();
    return (
        <>
            {!isUsSite && !isEUSite && (
                <div className='row'>
                    <div className='col-auto'>
                        <TextButtonInput
                            elementLinkId='find-address'
                            name='find-address'
                            label={t('find_address_label')}
                            error={searchPostCodeError}
                            value={searchPostCode}
                            onChange={onChangeSearchPostCode}
                            onButtonClick={onFindClick}
                            buttonText={t('find_address')}
                            waitValue={finding}
                            waitOnClick={true}
                            placeHolder={t('enter_postcode')}
                            className='postcode-finder'
                            buttonDisabled={finding}
                        />
                    </div>
                    {addressListPanel}
                </div>
            )}

            {manualAddressButtonPanel}
            {addressPanel}
        </>
    );
};

export default FindAddress;
